/**
 * The reference sheet.
 *
 * Built from the same data the generator uses, not written out again. A style
 * added to `MOODS` or a beat added to `GROOVES` documents itself, and a glossary
 * that can drift from the thing it describes is worse than none: it is
 * confidently wrong and nobody finds out.
 *
 * Prose only appears where the data cannot say it. What dorian *feels* like is
 * not derivable from a list of intervals, so those descriptions are written; the
 * names, tempos, patterns and instrument lists are all read from source.
 */

import { INSTRUMENTS_BY_ROLE } from "../src/engine.js";
import { GROOVES } from "../src/grooves.js";
import { creditLine } from "../src/licence.js";
import { MOOD_SUMMARIES } from "../src/randomise.js";
import { MODE_NAMES } from "../src/theory.js";

/** What a mode does to the mood. The one thing the interval table cannot say. */
const MODE_NOTES: Record<string, string> = {
  major: "Bright and settled. The default sound of resolution.",
  minor: "Darker, heavier. Most of the calm colony music lives here.",
  dorian: "Minor with a lifted sixth, so it is sad without being bleak. Good for drifting.",
  phrygian: "Minor with a flattened second, which is the one that sounds uneasy.",
  lydian: "Major with a raised fourth. Wonder, or something slightly off.",
  mixolydian: "Major with a flattened seventh. Warm, folky, never quite resolves.",
};

/** What each layer is for. */
const LAYER_NOTES: [string, string][] = [
  ["pad", "Held chords underneath everything. Always present, because silence reads as the game having stopped rather than as calm."],
  ["bass", "One note per chord, low. The floor the rest sits on."],
  ["perc", "Drums. The only layer that is written out rather than generated."],
  ["rhythm", "A repeating figure, mostly on chord tones. The difference between an ostinato and a wrong note played confidently."],
  ["melody", "The tune on top. It enters on the second pass through the chords, so the piece states itself before it decorates."],
];

const TERMS: [string, string][] = [
  [
    "Pressure",
    "How bad things are, from 0 to 1. Every layer has a pressure it arrives at, and the game feeds this number in from what is actually happening. Nothing is composed while the game runs: the layers all play all of the time and only their volume changes.",
  ],
  [
    "Enters at",
    "The pressure a layer becomes audible at. A pad at 0 is always there. Percussion at 0.7 only turns up when something is wrong. In the upbeat styles this is inverted, and the beat arrives at 0 while the melody waits.",
  ],
  [
    "Chords",
    "Roman numerals, one per chord. Case carries meaning exactly as it does on paper: VI is a major chord, vi is minor. Add a 7 for a seventh. The notes are worked out from the key, so the same numerals in a different key are a different set of notes and the same shape.",
  ],
  [
    "Bars per chord",
    "How long each chord is held. This changes the character more than tempo does: the same chords at the same speed are ambient at four bars each and a song at one.",
  ],
  [
    "Figures",
    "How many distinct phrases a layer invents before it starts repeating them. One is an ostinato. Two or three is a phrase and an answer. Set it as high as the number of chords and the layer never repeats itself, which sounds like a wash however good each phrase is.",
  ],
  [
    "Variation",
    "How much a phrase is allowed to drift each time it comes back. At 0 it is literal, which turns mechanical over a long loop. Past about 0.4 it stops being a phrase at all.",
  ],
  [
    "Density",
    "How busy a layer is. For a phrase it is how many of the available slots get used; for an arpeggio it picks the subdivision, because a sparse arpeggio is just a slow one.",
  ],
  [
    "Arpeggio and pulse",
    "Alternatives to phrases. An arpeggio runs steadily through the chord and a pulse repeats one note. Both are mechanical on purpose, and it is most of what makes a piece sound driven rather than played.",
  ],
  [
    "Seed",
    "The number every random choice comes from. The same seed always gives the same track, which is why a piece is the handful of settings that describe it rather than an audio file you have to keep forever. It is also the only way to keep the good one after pressing the button forty times.",
  ],
  [
    "Loop seamlessly",
    "Drops the fades and rounds the length up to a whole number of trips through the chords. Both are needed: a fade dips audibly every time round, and a length that stops mid-progression loops back to the first chord from the wrong place. The rounding is not small, so the length you asked for is rarely the length you get.",
  ],
  [
    "Export pack",
    "A zip with one audio file per layer, a mixdown to listen to, and a small manifest holding the tempo, the bar length and each layer's threshold. That is what a game engine needs to mix the layers itself. There is a licence in there too, see below.",
  ],
  [
    "Copy link",
    "Every track is a URL. A track you have only rolled gets a short one carrying the style and the seed; one you have changed gets a longer one carrying the whole piece. Either opens exactly what you were hearing, so a track you like is something you can send to someone.",
  ],
];

const el = (tag: string, cls?: string, text?: string): HTMLElement => {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (text !== undefined) node.textContent = text;
  return node;
};

function section(title: string): HTMLElement {
  return el("h3", "help-h", title);
}

function entry(term: string, body: string): HTMLElement {
  const wrap = el("div", "help-entry");
  wrap.append(el("dt", undefined, term), el("dd", undefined, body));
  return wrap;
}

/** Print a groove the way the editor prints it, so the two agree on sight. */
function grooveBlock(id: string): HTMLElement {
  const groove = GROOVES[id];
  const wrap = el("div", "help-groove");
  wrap.append(el("h4", undefined, id === groove.name ? id : `${id} — ${groove.name}`));

  if (!groove.voices.length) {
    wrap.append(el("p", "help-note", "No percussion at all. What the calm styles use."));
    return wrap;
  }

  const pre = el("pre");
  pre.textContent = groove.voices
    .map((v) => `${v.drum.padEnd(9)} ${v.steps.replace(/\./g, "·")}`)
    .join("\n");
  wrap.append(pre);

  const facts: string[] = [];
  if (groove.stepsPerBar && groove.stepsPerBar !== 16) {
    facts.push(`${groove.stepsPerBar} steps to the bar, so it swings in triplets`);
  }
  if (groove.fill?.length) facts.push("has a fill on the last bar of each cycle");
  if (facts.length) wrap.append(el("p", "help-note", `${facts.join(", ")}.`));

  return wrap;
}

export function buildHelp(): void {
  const body = document.getElementById("help-body");
  if (!body) return;
  body.replaceChildren();

  // --- what it is ---
  body.append(section("What this actually does"));
  const intro = el("p", "help-lede");
  intro.textContent =
    "You describe constraints, not notes: a key, some chords, how busy each part is. " +
    "Everything inside those constraints is chosen when it renders, which is why it can be " +
    "rolled again and still be music. It comes out as separate layers rather than one track, " +
    "so a game can fade them in as things get worse.";
  body.append(intro);

  // --- terms ---
  body.append(section("The settings"));
  const terms = el("dl", "help-list");
  for (const [term, text] of TERMS) terms.append(entry(term, text));
  body.append(terms);

  // --- layers ---
  body.append(section("The five layers"));
  const layers = el("dl", "help-list");
  for (const [role, text] of LAYER_NOTES) {
    const instruments = INSTRUMENTS_BY_ROLE[role] ?? [];
    layers.append(entry(role, `${text} Instruments: ${instruments.join(", ")}.`));
  }
  body.append(layers);

  // --- modes ---
  body.append(section("Modes"));
  const modes = el("dl", "help-list");
  for (const mode of MODE_NAMES) {
    modes.append(entry(mode, MODE_NOTES[mode] ?? ""));
  }
  body.append(modes);

  // --- styles ---
  body.append(section("The feelings"));
  const styles = el("dl", "help-list");
  for (const mood of MOOD_SUMMARIES) {
    const bits = [
      `${mood.mode}, ${mood.tempo[0]} to ${mood.tempo[1]} bpm`,
      `${[...new Set(mood.barsPerChord)].join(" or ")} bars a chord`,
      mood.grooves.length ? `beats: ${mood.grooves.join(", ")}` : "no drums",
    ];
    styles.append(entry(mood.name, `${bits.join(". ")}.`));
  }
  body.append(styles);

  // --- grooves ---
  body.append(section("The beats"));
  const legend = el("p", "help-note");
  legend.textContent =
    "Sixteen steps to a bar, four to a beat. X is an accent, x is a hit, a dot is a rest. " +
    "You can edit any of these on the page: tap a step to cycle it.";
  body.append(legend);
  for (const id of Object.keys(GROOVES)) body.append(grooveBlock(id));

  // --- licence ---
  body.append(section("Using what you make"));
  const licence = el("p");
  licence.textContent =
    "Anything you export is yours to use, including in something you sell, under a " +
    "Creative Commons Attribution licence. The one condition is a credit somewhere a " +
    "person can find it, such as a credits screen or a store page:";
  body.append(licence);

  const credit = el("pre", "help-credit", creditLine());
  body.append(credit);

  const licenceNote = el("p", "help-note");
  licenceNote.textContent =
    "The full terms travel with every export, in a file called READ ME AND LICENCE.txt. " +
    "You do not need to ask permission and you owe nothing else.";
  body.append(licenceNote);

  // --- privacy ---
  body.append(section("Where your work goes"));
  const privacy = el("p");
  privacy.textContent =
    "Nowhere. Every note is synthesised in this tab, there is no server behind this page, " +
    "and once it has loaded it works with the network switched off. Nothing you type is sent " +
    "anywhere because there is nowhere for it to be sent.";
  body.append(privacy);
}
